/**
 * A picture somebody dropped on the chat, turned into something worth sending.
 *
 * This runs in the tab, before a single byte leaves it, and it is the reason
 * an upload feature can exist here at all. A phone photograph is four to eight
 * megabytes; the thing a learner actually wants to show — a screenshot of a
 * stack trace, a terminal with the wrong branch in it, a graph that looks
 * wrong — is legible at six hundred pixels and weighs forty kilobytes once it
 * has been through this. That ratio is the whole cost model: the server's
 * ceilings (`worker/chat-images.ts`) are what make overspending impossible,
 * and this is what makes hitting them unlikely.
 *
 * ## Everything is re-encoded, including a file that was already small enough
 *
 * The tempting optimisation is to pass a small PNG through untouched and save
 * a decode. It is refused here, and not for a size reason.
 *
 * A JPEG out of a phone carries EXIF, and EXIF carries where the photograph
 * was taken. A learner dropping a picture of their screen into a room has not
 * agreed to publish their home address, and would have no way of knowing they
 * had: nothing in the panel would look different. Drawing the image onto a
 * canvas and reading it back out is the one operation that reliably leaves
 * every byte of metadata behind, because what comes out is pixels the browser
 * composited and not a file anybody wrote.
 *
 * It buys two smaller things as well. Orientation is baked in rather than
 * carried as a tag the next reader may or may not honour, so a photograph is
 * the way up it looked in the picker. And the output type is one type, chosen
 * here, so the thing the server sniffs is the thing this produced.
 *
 * ## Why the dimensions are checked before the canvas is made
 *
 * A decompression bomb is a small file that decodes enormous — a few kilobytes
 * of PNG can describe thirty thousand pixels square, which is a canvas of
 * about four gigabytes. `createImageBitmap` is what does the decoding and it
 * is the browser's own, so the bomb does not reach anything of ours; what it
 * can still do is take the tab down, and taking the tab down here means taking
 * the machine, the terminal and an unsaved editor buffer down with it. So the
 * bitmap's own dimensions are read and refused before a canvas is allocated,
 * which is the last moment at which refusing is free.
 *
 * ## What it does not do
 *
 * It does not look at the picture. There is no moderation here, no
 * classification and no scanning, and there could not honestly be any in a
 * browser — anything this file decided could be turned off by anyone willing
 * to open the console, and a check that is advisory is worse than none because
 * it reads like a guarantee. What protects a room is that an image hangs off
 * an account that can be banned, that it can be reported with the picture
 * attached, and that removing it destroys the bytes.
 */

import { cleanImageId, type ChatImage } from '@engineer-os/chat';
import { chatUrl } from './chat';

/** What the server will take, and what this produces. See `worker/chat-images.ts`. */
export const IMAGE_TYPE = 'image/webp';

/**
 * The longest edge, in pixels, of anything this sends.
 *
 * Six hundred and forty is chosen against the panel rather than against a
 * screen: the chat is between 280 and 900 CSS pixels wide (`CHAT_MIN` /
 * `CHAT_MAX` in lib/chat.ts), and a thumbnail in it is smaller again. At two
 * device pixels per CSS pixel this still has more detail than the panel can
 * draw, and the full-size view is a picture on a phone rather than a document
 * to be zoomed into. A learner who needs somebody to read a stack trace pastes
 * the text, which is what the room is for.
 */
export const MAX_EDGE = 640;

/**
 * What one image may weigh after all of this, in bytes.
 *
 * The same number the Worker enforces, and deliberately the same: a tab that
 * sent something the server then refused would have spent the learner's
 * upload and their patience to find out. The Worker is still the authority —
 * this is the tab saying no sooner, which is the pattern the whole protocol
 * uses for names and message lengths.
 */
export const MAX_BYTES = 256 * 1024;

/**
 * The most pixels this will decode, as width times height.
 *
 * Forty megapixels is past any camera a learner is carrying and nowhere near
 * the size at which a canvas becomes a problem, which is the gap this wants to
 * sit in: generous enough that nothing real is refused, small enough that a
 * bomb is.
 */
const MAX_PIXELS = 40_000_000;

/** Why an image was refused. The panel turns these into sentences; this file writes none. */
export type ImageRefusal =
  /** Not a picture, or not one this browser can decode. */
  | 'not-an-image'
  /** Decodes to more pixels than anything real has. */
  | 'too-many-pixels'
  /** This browser has no canvas encoder for the type we send. */
  | 'no-encoder'
  /** Still over the cap at the smallest quality worth sending. */
  | 'too-heavy'
  /** This deployment is not taking pictures. The routes answer 404 while the
   *  switch is off, which is the same answer as a route that does not exist —
   *  so this is "not here", not "broken". */
  | 'off'
  /** This account has posted as many pictures today as it may. */
  | 'daily'
  /** The deployment is holding as much as it has agreed to hold. Nothing the
   *  learner did, and nothing they can fix. */
  | 'full'
  /** The upload did not reach the server, or the answer did not come back. */
  | 'offline';

/** Thrown, rather than returned as null, so the reason survives the call. */
export class ImageRefused extends Error {
  constructor(readonly reason: ImageRefusal) {
    super(reason);
    this.name = 'ImageRefused';
  }
}

/** What comes out: bytes to send, and the shape the panel reserves for them. */
export interface PreparedImage {
  blob: Blob;
  width: number;
  height: number;
  type: typeof IMAGE_TYPE;
}

/**
 * The qualities tried, in order, and where they stop.
 *
 * Descending rather than a binary search, because the first one almost always
 * wins for the pictures this is for — a screenshot at 640px is flat colour and
 * compresses hard — and a search would cost several encodes to find that out.
 * It stops at 0.5 because below that WebP starts eating the text in a
 * screenshot, which is the one thing the picture was sent to show; if 0.5 is
 * still too heavy the image is scaled down instead, which loses detail evenly
 * rather than destroying the part that matters.
 */
const QUALITIES = [0.82, 0.7, 0.6, 0.5];

/**
 * A file from a drop, a paste or a picker, ready to upload — or a throw
 * saying why not.
 *
 * The order is: refuse what is obviously not a picture, decode, refuse what
 * decodes too big, scale, then encode at falling quality and finally at
 * falling size. Every refusal happens at the earliest point it can, because
 * the cheapest refusal is the one that never allocated anything.
 */
export async function prepareImage(file: Blob): Promise<PreparedImage> {
  // The declared type, which is a hint and not a fact — the server sniffs the
  // bytes and this does not pretend otherwise. It is checked anyway because it
  // costs nothing and it turns "dropped a PDF" into an immediate, accurate
  // sentence rather than a decode failure.
  if (file.type && !file.type.startsWith('image/')) throw new ImageRefused('not-an-image');

  let bitmap: ImageBitmap;
  try {
    // `from-image` rather than the default, so a photograph taken sideways
    // arrives the way up it was taken. The default is `from-image` in current
    // browsers and was not always, and this is a case where being explicit
    // costs one property and removes a whole class of "why is it rotated".
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // An SVG reaches here too, on the browsers that refuse to decode one into
    // a bitmap, and this is a fine place for it to stop — though it is not the
    // place that matters. The server refuses SVG by sniffing the bytes, and it
    // has to, because nothing decided in a tab is a defence.
    throw new ImageRefused('not-an-image');
  }

  try {
    if (bitmap.width * bitmap.height > MAX_PIXELS) throw new ImageRefused('too-many-pixels');

    // The scale that brings the longest edge to MAX_EDGE, and never above 1:
    // a picture smaller than that is left at its own size rather than blown
    // up, which would cost bytes to add nothing.
    let scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));

    // Four attempts at most, and each one is a strictly smaller picture than
    // the last. The loop is bounded by the array rather than by a condition on
    // the size, because a loop that keeps halving until something fits is a
    // loop that can be made not to terminate by an adversarial input.
    for (const step of [1, 0.75, 0.55, 0.4]) {
      const width = Math.max(1, Math.round(bitmap.width * scale * step));
      const height = Math.max(1, Math.round(bitmap.height * scale * step));
      const blob = await encode(bitmap, width, height);
      if (blob.size <= MAX_BYTES) return { blob, width, height, type: IMAGE_TYPE };
    }
    throw new ImageRefused('too-heavy');
  } finally {
    // The decoded bitmap can be tens of megabytes and nothing else holds it.
    // `close` is what hands that back now rather than at the next collection,
    // which matters in a tab that is also running an emulator.
    bitmap.close();
  }
}

/**
 * One size, at the best quality that fits — or the smallest tried.
 *
 * The canvas is made per attempt rather than reused, because a reused canvas
 * has to be cleared and a cleared canvas of the wrong size has to be resized,
 * and both of those are the same allocation with a bug attached.
 */
async function encode(bitmap: ImageBitmap, width: number, height: number): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageRefused('no-encoder');
  // The browser's own resampler. It is not the best one there is, and the
  // better one is several hundred lines of Lanczos that would run on the main
  // thread beside an emulator; `high` is what this is for.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);

  let smallest: Blob | null = null;
  for (const quality of QUALITIES) {
    const blob = await toBlob(canvas, quality);
    // A browser with no WebP encoder answers with a PNG and the type says so.
    // Refused rather than sent: the server takes PNG, but a PNG of a
    // photograph at 640px is several times the cap and would only fail later.
    if (blob.type !== IMAGE_TYPE) throw new ImageRefused('no-encoder');
    smallest = blob;
    if (blob.size <= MAX_BYTES) return blob;
  }
  // The last and smallest. The caller decides whether it fits; this function
  // only promises to have tried.
  return smallest!;
}

/** `canvas.toBlob` as a promise, refusing rather than resolving null. */
function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new ImageRefused('no-encoder'))), IMAGE_TYPE, quality);
  });
}

/**
 * The first image on a drop or a paste, or null.
 *
 * A `DataTransfer` from either carries several representations of the same
 * thing — a file, an HTML fragment naming it, a URL — and a drag from another
 * browser tab carries no file at all. Only `files` is taken: a URL would be a
 * request this tab makes to somebody else's server on the learner's behalf,
 * which is the exact thing `cleanWorn` refuses for avatars and for the same
 * reason (docs/contracts.md, "Faces and operators").
 *
 * One, not all of them. A message carries one picture, and a learner who drags
 * four in has not asked to post four messages.
 */
export function imageFrom(data: DataTransfer | null): File | null {
  if (!data) return null;
  for (const file of Array.from(data.files)) {
    if (file.type.startsWith('image/')) return file;
  }
  return null;
}

/**
 * Whether a drag carries a file at all, for the panel deciding whether to
 * light up.
 *
 * `items` and not `files`, because during a drag — before the drop — the file
 * list is deliberately empty and only the *kinds* are readable. A drop target
 * that waited for `files` would never show a learner that it was a target.
 */
export function dragHasFile(data: DataTransfer | null): boolean {
  if (!data) return false;
  return Array.from(data.items).some((item) => item.kind === 'file');
}

/**
 * Prepare a file and put it in the bucket, answering with what a message
 * needs to carry it.
 *
 * Two steps and one function, because there is no useful state between them:
 * a prepared blob nobody uploaded is bytes in memory, and the panel has
 * nothing to do with one. What the panel wants is a picture it can send or a
 * sentence saying why not, and this is the shape of that.
 *
 * The bytes go up as the request body rather than as a `multipart/form-data`
 * part or a base64 field, and the shape is deliberate: multipart would make
 * the server parse an envelope before it could count a byte, and base64 would
 * add a third to a payload whose whole design is about being small. The two
 * numbers that cannot ride in the body ride in the query string.
 *
 * Every refusal the server can give is turned into one of ours here, so the
 * panel never has to know a status code. That matters most for 404: the
 * upload route does not exist while the deployment has pictures switched off,
 * and "not here" is a different thing to tell somebody than "it broke".
 */
export async function uploadImage(file: Blob): Promise<ChatImage> {
  const ready = await prepareImage(file);

  let response: Response;
  try {
    response = await fetch(chatUrl(`/api/chat/images?w=${ready.width}&h=${ready.height}`), {
      method: 'POST',
      // The type the canvas actually produced, which the server checks against
      // the bytes rather than believing. Sent anyway: a request whose declared
      // type disagrees with its content is a thing worth being able to see in
      // a log, and getting it right costs nothing.
      headers: { 'Content-Type': ready.type },
      body: ready.blob,
      // Same origin, so the session cookie rides along. Stated rather than
      // left to the default, because the default differs between browsers and
      // an upload that is silently anonymous is an upload that is silently
      // refused.
      credentials: 'same-origin',
    });
  } catch {
    // A dropped connection, a blocked request, a tab going to sleep mid-send.
    // None of them is the learner's fault and all of them are worth retrying.
    throw new ImageRefused('offline');
  }

  if (!response.ok) throw new ImageRefused(refusalFor(response.status));

  let body: { url?: unknown; width?: unknown; height?: unknown };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    throw new ImageRefused('offline');
  }

  // The id is taken out of the URL the server built rather than out of a
  // separate field, so there is one string that can be wrong instead of two —
  // and it is put through the protocol's own validator, because everything
  // else in this product that comes off the wire is.
  const url = typeof body.url === 'string' ? body.url : '';
  const id = cleanImageId(url.startsWith(UPLOAD_PATH) ? url.slice(UPLOAD_PATH.length) : '');
  if (!id) throw new ImageRefused('offline');

  return {
    id,
    // The server's numbers, not the ones just measured: they are the same
    // today, and if they ever are not, the row should agree with the record.
    w: typeof body.width === 'number' ? body.width : ready.width,
    h: typeof body.height === 'number' ? body.height : ready.height,
  };
}

/** Where a served picture's path starts, and therefore where its id does. */
const UPLOAD_PATH = '/api/chat/images/';

/**
 * A status from the upload route, as something the panel can say.
 *
 * Each of these has a different thing the person can do about it, which is why
 * they are not one "upload failed". 413 and 415 are about this file and mean
 * try another; 429 is about today and means come back; 507 is about the
 * deployment and means there is nothing to try; 404 means the feature is not
 * on here at all. Anything else is a bug and is reported as a network fault,
 * which is the honest description of "the server said something unexpected".
 */
function refusalFor(status: number): ImageRefusal {
  if (status === 404) return 'off';
  if (status === 413) return 'too-heavy';
  if (status === 415) return 'not-an-image';
  if (status === 429) return 'daily';
  if (status === 507) return 'full';
  return 'offline';
}
